import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// Read-only executive opening-hours overview aggregated per location,
// including the embedded Google Business comparison.
const useOpeningHoursOverview = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const getOpeningHoursOverview = useCallback(
    async ({ months } = {}) => {
      const params = months ? `?months=${encodeURIComponent(months)}` : '';
      return await sendRequest({
        url: `/api/v1/openingHour/overview${params}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  // Impact analysis for a schedule: which entertainments (per branch) use it
  // and which other weekdays share an hours entry with the given weekday.
  const getOpeningHourImpact = useCallback(
    async (openingHourId, weekday) => {
      const params = weekday !== undefined ? `?weekday=${encodeURIComponent(weekday)}` : '';
      return await sendRequest({
        url: `/api/v1/openingHour/${openingHourId}/impact${params}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  // Set the hours of one weekday in place (affects every usage of the schedule).
  const updateOpeningHourDay = useCallback(
    async (openingHourId, weekday, intervals) => {
      return await sendRequest({
        url: `/api/v1/openingHour/${openingHourId}/day/${weekday}`,
        method: 'PATCH',
        headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: { intervals },
      });
    },
    [authCtx, sendRequest]
  );

  // Decouple a day change into a new schedule for a single entertainment.
  const decoupleOpeningHourDay = useCallback(
    async (openingHourId, { entertainmentId, weekday, intervals, newName }) => {
      return await sendRequest({
        url: `/api/v1/openingHour/${openingHourId}/decouple`,
        method: 'POST',
        headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: { entertainmentId, weekday, intervals, newName },
      });
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    getOpeningHoursOverview,
    getOpeningHourImpact,
    updateOpeningHourDay,
    decoupleOpeningHourDay,
  };
};

export default useOpeningHoursOverview;
